import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/pages/admin/AdminShell";
import { StatCard, Section } from "@/pages/Shell";
import { PaymentsAdmin } from "@/pages/admin/AdminDashboard";
import { api, formatError } from "@/lib/api";
import BudgetVsActual from "@/components/admin/BudgetVsActual";
import FinanceAnomalies from "@/components/admin/FinanceAnomalies";
import FinanceSummaryAI from "@/components/admin/FinanceSummaryAI";
import ProjectFinanceDocs from "@/components/admin/ProjectFinanceDocs";
import { useFocusRow } from "@/hooks/useFocusRow";
import {
  TrendUp, TrendDown, Wallet, Clock, WarningOctagon, Calendar,
  MagnifyingGlass, Receipt, User as UserIcon, Gear, Paperclip
} from "@phosphor-icons/react";

const ACCENT = "#1C1C1E";
const CATEGORY_TO_TYPE = (cat) => {
  const c = (cat || "").toLowerCase();
  if (["labor", "work", "builder", "construccion", "construction", "mano_obra"].includes(c)) return "builder";
  if (["internal", "admin", "office", "interno"].includes(c)) return "internal";
  // default: materials, supplier, etc.
  return "supplier";
};

const TYPE_LABEL = {
  investor: "Inversor",
  builder: "Constructor",
  supplier: "Proveedor",
  internal: "Interno",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function fmtMoney(n, cur = "EUR") {
  return `€${Math.round(Number(n) || 0).toLocaleString()}`;
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [villas, setVillas] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [activeType, setActiveType] = useState("investor");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [p, inv, u, v, n] = await Promise.all([
        api.get("/payments"),
        api.get("/invoices"),
        api.get("/users"),
        api.get("/villas"),
        api.get("/payment-notifications?status=pending").catch(() => ({ data: [] })),
      ]);
      setPayments(p.data); setInvoices(inv.data);
      setUsers(u.data); setVillas(v.data); setNotifs(n.data);
    } catch (e) { console.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useFocusRow([payments, invoices]);

  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.user_id, u])), [users]);
  const villasById = useMemo(() => Object.fromEntries(villas.map(v => [v.villa_id, v])), [villas]);

  // ---------- Derived financial metrics (REAL) ----------
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const m = useMemo(() => {
    let paid = 0, pending = 0, overdue = 0, next30 = 0, cashIn = 0, cashOut = 0;
    payments.forEach(p => {
      const amt = Number(p.amount) || 0;
      const d = daysUntil(p.due_date);
      if (p.status === "paid") { paid += amt; cashIn += amt; }
      else if (p.status === "overdue" || (p.status === "pending" && d !== null && d < 0)) { overdue += amt; pending += amt; }
      else if (p.status === "pending") {
        pending += amt;
        if (d !== null && d >= 0 && d <= 30) next30 += amt;
      }
    });
    invoices.forEach(i => { cashOut += Number(i.amount) || 0; });
    return { paid, pending, overdue, next30, cashIn, cashOut, net: cashIn - cashOut };
  }, [payments, invoices]);

  // ---------- Upcoming due dates (next 60 days, pending) ----------
  const upcoming = useMemo(() => {
    return payments
      .filter(p => p.status !== "paid")
      .map(p => ({ ...p, _days: daysUntil(p.due_date) }))
      .filter(p => p._days !== null && p._days >= -7 && p._days <= 60)
      .sort((a, b) => a._days - b._days)
      .slice(0, 10);
  }, [payments]);

  // ---------- Grouped rows by type ----------
  const rowsByType = useMemo(() => {
    const investor = payments.map(p => ({
      _kind: "payment",
      id: p.payment_id,
      type: "investor",
      party: usersById[p.investor_user_id]?.name || p.investor_user_id || "—",
      villa: villasById[p.villa_id]?.name || p.villa_id || "—",
      concept: p.phase || "Pago fase",
      due_date: p.due_date,
      amount: p.amount, currency: p.currency,
      status: (() => {
        if (p.status === "paid") return "paid";
        const d = daysUntil(p.due_date);
        if (p.status === "pending" && d !== null && d < 0) return "overdue";
        return p.status || "pending";
      })(),
    }));
    const fromInvoices = invoices.map(i => ({
      _kind: "invoice",
      id: i.invoice_id,
      type: CATEGORY_TO_TYPE(i.category),
      party: i.provider || "—",
      villa: villasById[i.villa_id]?.name || "—",
      concept: i.concept || i.category || "—",
      due_date: i.due_date || i.date,
      amount: i.amount, currency: i.currency,
      status: i.status || "registered",
      _hasDueDate: !!i.due_date,
      _category: i.category || "materials",
    }));
    const all = [...investor, ...fromInvoices];
    return {
      investor: all.filter(r => r.type === "investor"),
      builder:  all.filter(r => r.type === "builder"),
      supplier: all.filter(r => r.type === "supplier"),
      internal: all.filter(r => r.type === "internal"),
    };
  }, [payments, invoices, usersById, villasById]);

  const filteredRows = useMemo(() => {
    let rows = rowsByType[activeType] || [];
    if (statusFilter !== "all") rows = rows.filter(r => r.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(r =>
        [r.party, r.villa, r.concept].some(x => (x || "").toLowerCase().includes(s))
      );
    }
    return rows;
  }, [rowsByType, activeType, statusFilter, search]);

  // ---------- Invoice status updater (inline) ----------
  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await api.patch(`/invoices/${invoiceId}`, { status });
      load();
    } catch (e) { alert(formatError(e)); }
  };

  // ---------- Invoice archive/delete (Admin Master Controls — P0) ----------
  const archiveInvoice = async (inv) => {
    const ok = window.confirm(
      `¿Archivar la factura de "${inv.provider || "—"}" (€${Number(inv.amount || 0).toLocaleString()})?\n\n` +
      "La factura desaparecerá del listado y dejará de contar en cash-out y presupuestos. " +
      "Es reversible: cambia el estado de la factura desde la base de datos si necesitas restaurarla."
    );
    if (!ok) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/archive`);
      load();
    } catch (e) { alert(formatError(e)); }
  };

  const deleteInvoice = async (inv) => {
    const ok = window.confirm(
      `¿Eliminar permanentemente la factura de "${inv.provider || "—"}" (€${Number(inv.amount || 0).toLocaleString()})?\n\n` +
      "Esta acción es IRREVERSIBLE. También borrará los archivos adjuntos asociados."
    );
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.invoice_id}?confirm=true`);
      load();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <AdminShell>
      {/* Hero */}
      <div className="mb-8 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Finanzas</div>
        <h1 className="font-serif-dv text-5xl">Visión financiera global.</h1>
        <p className="text-stone-500 mt-2">Pagos de inversores, gastos de obra y flujo neto en un solo lugar.</p>
      </div>

      {loading && (
        <div className="dv-card py-6 mb-6 text-center text-stone-500 text-sm" data-testid="finance-loading">
          Cargando datos financieros…
        </div>
      )}

      {notifs.length > 0 && (
        <NotificationsPanel notifs={notifs} onRefresh={load} />
      )}

      {/* Plan vs Gasto — Finance Intelligence (F-27) */}
      <BudgetVsActual />

      {/* Documentos del proyecto — Finance Uploads (F-31) */}
      <ProjectFinanceDocs />

      {/* Anomalías detectadas — Finance Intelligence (F-28) */}
      <FinanceAnomalies />

      {/* Resumen IA on-demand — Finance Intelligence (F-30) */}
      <FinanceSummaryAI />

      {/* Summary cards — REAL */}
      <Section title="Indicadores">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total pagado" value={fmtMoney(m.paid)}
            sub={`${payments.filter(p => p.status === "paid").length} pagos`}
            accent={ACCENT} testid="finance-stat-paid"
          />
          <StatCard
            label="Pendiente" value={fmtMoney(m.pending)}
            sub={`${payments.filter(p => p.status !== "paid").length} por cobrar`}
            accent={ACCENT} testid="finance-stat-pending"
          />
          <StatCard
            label="Vencidos" value={fmtMoney(m.overdue)}
            sub="requiere acción"
            accent="#C1432A" testid="finance-stat-overdue"
          />
          <StatCard
            label="Próximos 30 días" value={fmtMoney(m.next30)}
            sub="pagos pendientes"
            accent={ACCENT} testid="finance-stat-next30"
          />
        </div>
      </Section>

      {/* Cash overview — REAL */}
      <Section title="Cash overview" subtitle="Liquidez real y compromiso de obra.">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="dv-card" data-testid="finance-cash-in" style={{ borderLeft: "3px solid #2F3E35" }}>
            <div className="flex items-center gap-2 mb-2">
              <TrendUp size={16} weight="bold" style={{ color: "#2F3E35" }} />
              <div className="text-xs uppercase tracking-widest text-stone-500">Cash in</div>
            </div>
            <div className="font-serif-dv text-4xl" style={{ color: "#2F3E35" }}>{fmtMoney(m.cashIn)}</div>
            <div className="text-xs text-stone-500 mt-2">Pagos de inversores recibidos</div>
          </div>
          <div className="dv-card" data-testid="finance-cash-out" style={{ borderLeft: "3px solid #9C5236" }}>
            <div className="flex items-center gap-2 mb-2">
              <TrendDown size={16} weight="bold" style={{ color: "#9C5236" }} />
              <div className="text-xs uppercase tracking-widest text-stone-500">Cash out</div>
            </div>
            <div className="font-serif-dv text-4xl" style={{ color: "#9C5236" }}>{fmtMoney(m.cashOut)}</div>
            <div className="text-xs text-stone-500 mt-2">Gastos de obra registrados ({invoices.length} facturas)</div>
          </div>
          <div className="dv-card" data-testid="finance-cash-net" style={{ borderLeft: `3px solid ${m.net >= 0 ? "#2F3E35" : "#C1432A"}` }}>
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={16} weight="bold" />
              <div className="text-xs uppercase tracking-widest text-stone-500">Net</div>
            </div>
            <div className="font-serif-dv text-4xl" style={{ color: m.net >= 0 ? "#2F3E35" : "#C1432A" }}>
              {m.net < 0 ? "−" : ""}{fmtMoney(Math.abs(m.net))}
            </div>
            <div className="text-xs text-stone-500 mt-2">In menos out (committed)</div>
          </div>
        </div>
      </Section>

      {/* Upcoming — REAL */}
      <Section title="Próximos vencimientos" subtitle="Pagos pendientes en los próximos 60 días.">
        <div className="dv-card p-0 overflow-hidden" data-testid="finance-upcoming">
          {upcoming.length === 0 ? (
            <div className="text-center py-10 text-stone-400">No hay vencimientos próximos.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-stone-50 border-b border-stone-200 text-left">
                <tr className="text-xs tracking-wider uppercase text-stone-500">
                  <th className="px-5 py-3">Vencimiento</th>
                  <th className="px-5 py-3">Inversor</th>
                  <th className="px-5 py-3">Villa</th>
                  <th className="px-5 py-3">Fase</th>
                  <th className="px-5 py-3 text-right">Importe</th>
                  <th className="px-5 py-3">Días</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map(p => {
                  const late = p._days < 0;
                  return (
                    <tr key={p.payment_id} className="border-b border-stone-100" data-testid={`upcoming-${p.payment_id}`} data-focus-id={p.payment_id}>
                      <td className="px-5 py-3">{p.due_date}</td>
                      <td className="px-5 py-3">{usersById[p.investor_user_id]?.name || "—"}</td>
                      <td className="px-5 py-3 text-stone-600">{villasById[p.villa_id]?.name || "—"}</td>
                      <td className="px-5 py-3">{p.phase}</td>
                      <td className="px-5 py-3 text-right font-serif-dv text-lg">{fmtMoney(p.amount)}</td>
                      <td className="px-5 py-3">
                        <span className={`dv-badge ${late ? "dv-badge-alta" : p._days <= 7 ? "dv-badge-media" : "dv-badge-baja"}`}>
                          {late ? `Vencido hace ${Math.abs(p._days)}d` : p._days === 0 ? "Hoy" : `En ${p._days}d`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </Section>

      {/* Filter + grouped table */}
      <Section title="Movimientos" subtitle="Filtrar por tipo, estado y búsqueda libre.">
        {/* Type switcher */}
        <div className="flex gap-1 mb-4 overflow-x-auto no-scrollbar">
          {["investor", "builder", "supplier", "internal"].map(t => {
            const isActive = activeType === t;
            const count = rowsByType[t]?.length || 0;
            return (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                data-testid={`finance-tab-${t}`}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap"
                style={{
                  background: isActive ? ACCENT : "transparent",
                  color: isActive ? "white" : "#57534E",
                  border: `1px solid ${isActive ? ACCENT : "#E7E5E4"}`,
                }}
              >
                {TYPE_LABEL[t]} <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              className="dv-input pl-10" placeholder="Buscar por persona, villa, concepto…"
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="finance-search"
            />
          </div>
          <select
            className="dv-input max-w-[200px]"
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="finance-status-filter"
          >
            <option value="all">Todos los estados</option>
            <option value="paid">Pagado</option>
            <option value="pending">Pendiente</option>
            <option value="overdue">Vencido</option>
            <option value="registered">Registrada</option>
          </select>
        </div>

        <div className="dv-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="finance-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs tracking-wider uppercase text-stone-500">
                <th className="px-5 py-3">{activeType === "investor" ? "Inversor" : "Proveedor"}</th>
                <th className="px-5 py-3">Villa</th>
                <th className="px-5 py-3">{activeType === "investor" ? "Fase" : "Categoría / Concepto"}</th>
                <th className="px-5 py-3">{activeType === "investor" ? "Vencimiento" : "Fecha"}</th>
                <th className="px-5 py-3 text-right">Importe</th>
                <th className="px-5 py-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-stone-400">
                  Sin movimientos en "{TYPE_LABEL[activeType]}" con los filtros actuales.
                </td></tr>
              ) : filteredRows.map(r => (
                <tr key={`${r._kind}-${r.id}`} className="border-b border-stone-100" data-testid={`finance-row-${r.id}`} data-focus-id={r.id}>
                  <td className="px-5 py-3 font-medium">{r.party}</td>
                  <td className="px-5 py-3 text-stone-600">{r.villa}</td>
                  <td className="px-5 py-3">{r.concept}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {r.due_date || "—"}
                    {r._kind === "invoice" && !r._hasDueDate && (
                      <span className="ml-1 text-[10px] text-stone-400 uppercase tracking-widest">(fecha)</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-serif-dv text-lg">{fmtMoney(r.amount)}</td>
                  <td className="px-5 py-3">
                    {r._kind === "invoice" ? (
                      <select
                        value={r.status}
                        onChange={e => updateInvoiceStatus(r.id, e.target.value)}
                        className="text-xs border border-stone-200 rounded px-2 py-1"
                        data-testid={`invoice-status-${r.id}`}
                      >
                        <option value="registered">Registrada</option>
                        <option value="pending">Pendiente</option>
                        <option value="paid">Pagada</option>
                        <option value="overdue">Vencida</option>
                      </select>
                    ) : (
                      <span className={`dv-badge ${r.status === "paid" ? "dv-badge-baja" : r.status === "overdue" ? "dv-badge-alta" : "dv-badge-media"}`}>
                        {r.status === "paid" ? "Pagado" : r.status === "overdue" ? "Vencido" : "Pendiente"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </Section>

      {/* Invoices block — REAL, with legacy fallback */}
      <Section title={`Facturas de obra (${invoices.length})`}
        subtitle="Proveedor, categoría, importe, vencimiento y estado. Los campos opcionales aparecen cuando se definen.">
        <div className="dv-card p-0 overflow-hidden" data-testid="finance-invoices">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[840px]">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs tracking-wider uppercase text-stone-500">
                <th className="px-4 py-3">Proveedor</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Concepto</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Vencimiento</th>
                <th className="px-4 py-3 text-right">Importe</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">Sin facturas aún.</td></tr>
              ) : invoices.map(i => (
                <tr key={i.invoice_id} className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium">{i.provider || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="dv-badge">{TYPE_LABEL[CATEGORY_TO_TYPE(i.category)]}</span>
                    <span className="text-xs text-stone-400 ml-1">{i.category || "materials"}</span>
                  </td>
                  <td className="px-4 py-3 text-stone-600">{i.concept || "—"}</td>
                  <td className="px-4 py-3">{i.date || "—"}</td>
                  <td className="px-4 py-3">
                    {i.due_date || <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-serif-dv text-lg">{fmtMoney(i.amount, i.currency)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={i.status || "registered"}
                      onChange={e => updateInvoiceStatus(i.invoice_id, e.target.value)}
                      className="text-xs border border-stone-200 rounded px-2 py-1"
                      data-testid={`invoice-block-status-${i.invoice_id}`}
                    >
                      <option value="registered">Registrada</option>
                      <option value="pending">Pendiente</option>
                      <option value="paid">Pagada</option>
                      <option value="overdue">Vencida</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => archiveInvoice(i)}
                      data-testid={`invoice-archive-${i.invoice_id}`}
                      title="Archivar factura"
                      className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 hover:bg-stone-900 hover:text-white mr-1"
                    >
                      Archivar
                    </button>
                    <button
                      onClick={() => deleteInvoice(i)}
                      data-testid={`invoice-delete-${i.invoice_id}`}
                      title="Eliminar factura permanentemente"
                      className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-700 hover:text-white hover:border-red-700"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </Section>

      {/* Payments CRUD — reuses existing PaymentsAdmin */}
      <Section title="Gestión de pagos (alta y edición)" subtitle="Registra nuevos pagos de inversores o actualiza su estado.">
        <PaymentsAdmin />
      </Section>
    </AdminShell>
  );
}

function NotificationsPanel({ notifs, onRefresh }) {
  const [confirmFor, setConfirmFor] = useState(null);
  const [dismissFor, setDismissFor] = useState(null);
  return (
    <Section
      title={`Notificaciones de pago pendientes (${notifs.length})`}
      subtitle="Inversores que reportan haber realizado su pago. Revisa y confirma."
    >
      <div className="space-y-3" data-testid="notif-panel">
        {notifs.map(n => (
          <div key={n.notification_id} className="dv-card border-l-4 border-amber-500" data-testid={`notif-${n.notification_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="text-xs tracking-widest uppercase text-stone-500 mb-1">
                  {n.villa_name || n.villa_id || "—"} · {n.payment?.phase || "—"}
                </div>
                <div className="font-serif-dv text-2xl">{n.investor_name}</div>
                <div className="text-stone-600 text-sm mt-1">
                  Reportó: <strong>€{Number(n.payment?.amount || 0).toLocaleString()} {n.payment?.currency || "EUR"}</strong>
                  {n.payment?.due_date && <> · vencimiento {n.payment.due_date}</>}
                </div>
                {n.reference && (
                  <div className="text-xs text-stone-500 mt-1">
                    Ref: <code className="bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5">{n.reference}</code>
                  </div>
                )}
                {n.receipt_file_id && (
                  <div className="text-xs mt-1.5">
                    <a
                      href={`${(import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api/files/${n.receipt_file_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[color:var(--dv-accent,#9C5236)] hover:underline"
                      data-testid={`notif-receipt-${n.notification_id}`}
                    >
                      <Paperclip className="w-3.5 h-3.5" /> Ver comprobante{n.receipt_filename ? ` · ${n.receipt_filename}` : ""}
                    </a>
                  </div>
                )}
                {n.comment && (
                  <div className="mt-2 p-2 bg-stone-50 rounded text-sm text-stone-700 italic">
                    "{n.comment}"
                  </div>
                )}
                <div className="text-xs text-stone-400 mt-2">
                  Notificado {new Date(n.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setDismissFor(n)} className="dv-btn dv-btn-ghost text-sm" data-testid={`notif-dismiss-${n.notification_id}`}>
                  Descartar
                </button>
                <button onClick={() => setConfirmFor(n)} className="dv-btn dv-btn-primary text-sm" data-testid={`notif-confirm-${n.notification_id}`}>
                  Confirmar pago
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {confirmFor && <ConfirmNotifModal notif={confirmFor} onClose={() => setConfirmFor(null)} onDone={async () => { setConfirmFor(null); await onRefresh(); }} />}
      {dismissFor && <DismissNotifModal notif={dismissFor} onClose={() => setDismissFor(null)} onDone={async () => { setDismissFor(null); await onRefresh(); }} />}
    </Section>
  );
}

function ConfirmNotifModal({ notif, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(today);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/payment-notifications/${notif.notification_id}/confirm`, { paid_at: paidAt });
      await onDone();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="confirm-notif-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Confirmar pago</div>
        <div className="font-serif-dv text-2xl mb-1">{notif.investor_name}</div>
        <div className="text-stone-500 text-sm mb-4">
          {notif.payment?.phase} · €{Number(notif.payment?.amount || 0).toLocaleString()} · {notif.villa_name || "—"}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500">Fecha real de pago *</label>
            <input
              type="date"
              required
              className="dv-input"
              value={paidAt}
              onChange={e => setPaidAt(e.target.value)}
              max={today}
              data-testid="confirm-paid-at"
            />
            <div className="text-xs text-stone-400 mt-1">
              Se guardará como fecha efectiva para reportes.
            </div>
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost">Cancelar</button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="confirm-submit">
              {busy ? "Confirmando…" : "Confirmar pago"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DismissNotifModal({ notif, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/payment-notifications/${notif.notification_id}/dismiss`, { review_note: note || null });
      await onDone();
    } catch (e) {
      alert(formatError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="dismiss-notif-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Descartar notificación</div>
        <div className="font-serif-dv text-2xl mb-4">{notif.investor_name}</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500">Motivo (opcional)</label>
            <textarea rows={3} className="dv-input" value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="Ej: No encuentro la transferencia en el banco" data-testid="dismiss-note" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost">Cancelar</button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="dismiss-submit" style={{ background: "#9C5236" }}>
              {busy ? "Descartando…" : "Descartar notificación"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
